import type { AdditionalData, SupplementalData } from '@dispatch/shared/types';
import { SupplementalDataTypes } from '@dispatch/shared/types';

type SupplementalField = {
  field: keyof AdditionalData;
  key: string;
  label: string;
  type: SupplementalDataTypes;
};

// Order is the render order on the task and the vendor email.
const SUPPLEMENTAL_FIELDS: readonly SupplementalField[] = [
  {
    field: 'referrersPhone',
    key: 'referral.referrersPhone',
    label: 'Referrers Phone',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'lineOfBusiness',
    key: 'referral.lineOfBusiness',
    label: 'Line of Business',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'strategicBusinessUnit',
    key: 'referral.strategicBusinessUnit',
    label: 'Strategic Business Unit',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'claimSystem',
    key: 'referral.claimSystem',
    label: 'Claim System',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'exposureNumber',
    key: 'referral.exposureNumber',
    label: 'Exposure Number',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'scopeOfWork',
    key: 'referral.scopeOfWork',
    label: 'Scope of Work',
    type: SupplementalDataTypes.MULTI_LINE_TEXT,
  },
  {
    field: 'typeOfReport',
    key: 'referral.typeOfReport',
    label: 'Type of Report',
    type: SupplementalDataTypes.MULTI_LINE_TEXT,
  },
  {
    field: 'budget',
    key: 'referral.budget',
    label: 'Budget',
    type: SupplementalDataTypes.MULTI_LINE_TEXT,
  },
  {
    field: 'fileInLitigation',
    key: 'referral.fileInLitigation',
    label: 'File in Litigation (Yes/No)',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'vehicleMake',
    key: 'vehicle.make',
    label: 'Make',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'vehicleModel',
    key: 'vehicle.model',
    label: 'Model',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'vehicleYear',
    key: 'vehicle.year',
    label: 'Year',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
  {
    field: 'vehicleVinOrSerial',
    key: 'vehicle.vinOrSerial',
    label: 'Serial or VIN Number',
    type: SupplementalDataTypes.SINGLE_LINE_TEXT,
  },
];

function formatValue(field: keyof AdditionalData, value: string): string {
  if (field === 'fileInLitigation') {
    return value === 'yes' ? 'Yes' : 'No';
  }

  return value;
}

export function buildSupplementalDataFromAdditionalData(
  data: AdditionalData | undefined
): SupplementalData[] {
  if (!data) {
    return [];
  }

  return SUPPLEMENTAL_FIELDS.flatMap(({ field, key, label, type }) => {
    const value = data[field]?.trim();

    if (!value) {
      return [];
    }

    return [{ key, label, type, value: formatValue(field, value) }];
  });
}
